import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import useScrollReveal from "../../hooks/useScrollReveal";

const NEXT_STEPS = [
  {
    title: "Kickoff email on its way",
    body: "Check your inbox in the next few minutes for your confirmation and a short note on what happens next.",
  },
  {
    title: "A strategist will reach out",
    body: "A Modern Exit strategist will personally review your submission and be in touch within one business day.",
  },
  {
    title: "Build begins within 48 hours",
    body: "Once we confirm scope and receive your assets, the build starts. You\u2019ll get a project tracker and direct line to the team.",
  },
];

export default function ThankYouPage() {
  useScrollReveal();
  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="thank-you-page">
      <SEO page="/thank-you" />
      <Nav />
      <main className="pt-28 sm:pt-32 md:pt-36 pb-20 px-5 sm:px-6 md:px-10">
        <div className="max-w-2xl mx-auto text-center reveal">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-olive/15 text-olive border border-olive/30">
            <CheckCircle2 size={26} strokeWidth={1.8} />
          </span>
          <p className="eyebrow mt-6 !text-clay">Received</p>
          <h1 data-kinetic className="font-display mt-4 text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em] font-semibold text-ink">
            Thank you.
          </h1>
          <p className="mt-5 text-[16px] sm:text-lg text-ink-muted leading-relaxed">
            Your request is in. A member of the Modern Exit team is already
            reviewing it and will be in touch shortly to move things forward.
          </p>
        </div>

        <div className="max-w-2xl mx-auto mt-16 space-y-4">
          {NEXT_STEPS.map((step, i) => (
            <div
              key={step.title}
              data-testid={`thank-you-step-${i}`}
              className="reveal flex items-start gap-4 rounded-3xl border border-sand-300 bg-sand-50 p-6 sm:p-7"
            >
              <span className="flex-shrink-0 h-9 w-9 rounded-full bg-clay/15 text-clay border border-clay/30 grid place-items-center font-mono text-[13px] font-semibold">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="font-display text-[18px] font-semibold text-ink leading-tight">
                  {step.title}
                </p>
                <p
                  className="mt-2 text-[14px] text-ink-muted leading-relaxed"
                >
                  {step.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto mt-14 reveal text-center">
          <p className="text-[14px] text-ink-muted">
            While you wait, learn more about how we work.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/packages"
              data-testid="thank-you-packages-cta"
              className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-5 py-3 text-[14px] text-ink hover:border-clay/50 transition-colors min-h-[44px]"
            >
              Explore packages
              <ArrowRight size={14} className="transition-transform" />
            </Link>
            <Link
              to="/"
              data-testid="thank-you-home-cta"
              className="inline-flex items-center gap-2 rounded-full bg-ink-deep text-sand-100 px-5 py-3 text-[14px] font-medium hover:bg-rust transition-colors min-h-[44px]"
            >
              Back to home
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
